import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  Text,
  Badge,
  Table,
  Modal,
  Textarea,
  Alert,
  Grid,
  Card,
  Divider,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconCheck, IconX, IconAlertCircle, IconRefresh, IconDownload } from "@tabler/icons-react";
import { adminVendorsApi, UpdateVendorStatusDto } from "../../../api/adminVendors";
import dayjs from "dayjs";

export function VendorDetail() {
  const navigate = useNavigate();
  const { vendorId } = useParams<{ vendorId: string }>();
  const queryClient = useQueryClient();
  const [approveModalOpened, setApproveModalOpened] = useState(false);
  const [rejectModalOpened, setRejectModalOpened] = useState(false);
  const [reactivateModalOpened, setReactivateModalOpened] = useState(false);
  const [rejectionComment, setRejectionComment] = useState("");

  const { data: vendor, isLoading } = useQuery({
    queryKey: ["admin", "vendor", vendorId],
    queryFn: () => adminVendorsApi.getVendor(vendorId!),
    enabled: !!vendorId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: (data: UpdateVendorStatusDto) =>
      adminVendorsApi.updateVendorStatus(vendorId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "vendors"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "vendor", vendorId] });
      notifications.show({
        title: "Success",
        message: "Vendor status updated successfully",
        color: "green",
      });
      setApproveModalOpened(false);
      setRejectModalOpened(false);
      setReactivateModalOpened(false);
      setRejectionComment("");
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update vendor status",
        color: "red",
      });
    },
  });

  const handleApprove = () => {
    updateStatusMutation.mutate({ status: "ACTIVE" });
  };

  const handleReject = () => {
    if (!rejectionComment.trim()) {
      notifications.show({
        title: "Error",
        message: "Please provide a reason for rejection",
        color: "red",
      });
      return;
    }
    updateStatusMutation.mutate({
      status: "SUSPENDED",
      rejectionComment: rejectionComment.trim(),
    });
  };

  const handleReactivate = () => {
    updateStatusMutation.mutate({ status: "ACTIVE" });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "PENDING":
        return "yellow";
      case "REGISTERED":
        return "blue";
      case "SUSPENDED":
        return "red";
      default:
        return "gray";
    }
  };

  const getBusinessTypeLabel = (type: string | null) => {
    if (!type) return "Not specified";
    const labels: Record<string, string> = {
      SOLE_PROPRIETOR: "Sole Proprietor",
      COMPANY: "Company",
      PARTNERSHIP: "Partnership",
    };
    return labels[type] || type;
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      BUSINESS_REGISTRATION: "Business Registration",
      COMPANY_PROFILE: "Company Profile",
      TAX_CLEARANCE: "Tax Clearance",
      INSURANCE: "Insurance",
      FINANCIAL_STATEMENT: "Financial Statement",
      OTHER: "Other",
    };
    return labels[type] || type;
  };


  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading vendor details...</Text>
      </Container>
    );
  }

  if (!vendor) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Error">
          Vendor not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin/vendors")}
          >
            Back to Vendor Management
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={2}>Vendor Details</Title>
              <Group>
                {vendor.status === "PENDING" && (
                  <>
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={() => setApproveModalOpened(true)}
                    >
                      Approve Vendor
                    </Button>
                    <Button
                      color="red"
                      leftSection={<IconX size={16} />}
                      onClick={() => setRejectModalOpened(true)}
                    >
                      Reject Vendor
                    </Button>
                  </>
                )}
                {vendor.status === "REGISTERED" && (
                  <Text size="sm" c="dimmed">
                    Vendor is completing profile and documents
                  </Text>
                )}
                {vendor.status === "SUSPENDED" && (
                  <Button
                    color="green"
                    leftSection={<IconRefresh size={16} />}
                    onClick={() => setReactivateModalOpened(true)}
                  >
                    Reactivate Vendor
                  </Button>
                )}
              </Group>
            </Group>

            <Divider />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="xs" padding="md" radius="md" withBorder>
                  <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                      Status
                    </Text>
                    <Badge color={getStatusColor(vendor.status)} size="lg">
                      {vendor.status === "REGISTERED"
                        ? "Registered"
                        : vendor.status === "PENDING"
                        ? "Pending Approval"
                        : vendor.status === "ACTIVE"
                        ? "Active"
                        : "Suspended"}
                    </Badge>

                    {vendor.rejectionComment && (
                      <Alert color="red" icon={<IconAlertCircle size={16} />}>
                        <Text size="sm" fw={500} mb="xs">
                          Rejection Reason:
                        </Text>
                        <Text size="sm">{vendor.rejectionComment}</Text>
                      </Alert>
                    )}
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="xs" padding="md" radius="md" withBorder>
                  <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                      Email Verified
                    </Text>
                    <Badge
                      color={vendor.emailVerified ? "green" : "yellow"}
                      size="lg"
                    >
                      {vendor.emailVerified ? "Verified" : "Not Verified"}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            <Divider />

            <Title order={3}>Business Information</Title>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Business Name
                </Text>
                <Text fw={500}>{vendor.businessName || "Not provided"}</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Email
                </Text>
                <Text>{vendor.email}</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Business Type
                </Text>
                <Text>{getBusinessTypeLabel(vendor.businessType)}</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Registration Number
                </Text>
                <Text>{vendor.registrationNumber || "Not provided"}</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Tax ID
                </Text>
                <Text>{vendor.taxId || "Not provided"}</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Phone
                </Text>
                <Text>{vendor.phone || "Not provided"}</Text>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" c="dimmed">
                  Address
                </Text>
                <Text>{vendor.address || "Not provided"}</Text>
              </Grid.Col>
            </Grid>

            <Divider />

            <Title order={3}>Documents</Title>
            {!vendor.documents || vendor.documents.length === 0 ? (
              <Text c="dimmed">No documents uploaded</Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Document Type</Table.Th>
                    <Table.Th>File Name</Table.Th>
                    <Table.Th>File Size</Table.Th>
                    <Table.Th>Uploaded At</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vendor.documents.map((doc) => (
                    <Table.Tr key={doc.id}>
                      <Table.Td>
                        <Text fw={500}>
                          {getDocumentTypeLabel(doc.documentType)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{doc.fileName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {(doc.fileSize / 1024).toFixed(2)} KB
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {dayjs(doc.uploadedAt).format("MMM DD, YYYY")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="Download Document">
                          <ActionIcon
                            color="blue"
                            variant="light"
                            onClick={async () => {
                              try {
                                const blob = await adminVendorsApi.downloadDocument(vendor.id, doc.id);
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', doc.fileName);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                                notifications.show({
                                  title: "Success",
                                  message: "Document downloaded successfully",
                                  color: "green",
                                });
                              } catch (error: any) {
                                notifications.show({
                                  title: "Error",
                                  message: error.response?.data?.error || "Failed to download document",
                                  color: "red",
                                });
                              }
                            }}
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Approve Modal */}
      <Modal
        opened={approveModalOpened}
        onClose={() => setApproveModalOpened(false)}
        title="Approve Vendor"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to approve this vendor account? An approval
            email will be sent to the vendor.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setApproveModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleApprove}
              loading={updateStatusMutation.isPending}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Modal */}
      <Modal
        opened={rejectModalOpened}
        onClose={() => setRejectModalOpened(false)}
        title="Reject Vendor"
      >
        <Stack gap="md">
          <Text>
            Please provide a reason for rejecting this vendor account. This
            comment will be sent to the vendor via email.
          </Text>
          <Textarea
            label="Rejection Reason"
            placeholder="Enter the reason for rejection..."
            required
            value={rejectionComment}
            onChange={(e) => setRejectionComment(e.currentTarget.value)}
            minRows={4}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setRejectModalOpened(false);
                setRejectionComment("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleReject}
              loading={updateStatusMutation.isPending}
              disabled={!rejectionComment.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reactivate Modal */}
      <Modal
        opened={reactivateModalOpened}
        onClose={() => setReactivateModalOpened(false)}
        title="Reactivate Vendor"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to reactivate this vendor account? A reactivation
            email will be sent to the vendor.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setReactivateModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleReactivate}
              loading={updateStatusMutation.isPending}
              leftSection={<IconRefresh size={16} />}
            >
              Reactivate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

